// The city switcher's list (Ben, Oct 10 2026): every city with its coordinates, region and points, and at the far right a
// heart that fills with the city's loyalty, the percentage under it. The city you are in is lit by a warm firelight
// glowing from its name (no "Here" tag). Shared by the world screens, the city screens and the map.
// Search, filters and sort (Ben, Oct 12 2026): a search field (name, coordinates, region or tag), SORT (name, coords,
// region, tag, Size (High) = most points first or Size (Low) = fewest first (Oct 14/15 2026) — kept on this device), REGION and TAG filters; each city shows its tags under its line. The search and
// filters start fresh every time the switcher opens. Enter in the search goes to the first city listed; with a mouse and
// keyboard the search has the focus as the list opens (on a phone it waits for a tap, so no keyboard pops up).
// Several tags at once (Ben, Oct 13 2026): the TAG filter is a row of the world's tag chips (and "No tag") under SORT and
// REGION; tap to light one, tap again to let go. With several lit, a city must wear them all.
// Incoming attacks (Ben, Oct 14 2026): a city with attacks coming gets a red edge and a red "⚔ N incoming · lands 0:06:04"
// line; one landing within 10 minutes gets the full red row, a glowing medallion and a blinking dot. While anything is
// incoming, an "Incoming" button joins SORT | REGION (soonest landing first, cut into Landing in 10 min / Within the
// hour / Later / No incoming — kept on this device), and summary chips (N land < 10 min · N under attack · N clear) sit
// under the tags and filter the list when tapped. The landing times come from each attacked city's village/get, read
// while the switcher is open (state/incoming.ts); until a city is read it shows its count without a time.
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { PlayerVillage } from "@/api/types";
import { fmtCoords, fmtNum, plural } from "@/game/format";
import {
  TIER_LABEL, URGENT_MS, filterIncoming, incomingCount, incomingGroups, incomingSummary, nextOf, sortByIncoming, totalIncoming, type IncomingFilter,
} from "@/game/incoming";
import { fmtDuration } from "@/game/time";
import { useNow } from "@/state/clock";
import { useIncomingBook } from "@/state/incoming";
import { CITY_SORTS, NO_TAG, cityList, regionCounts, regionLabel, tagCounts, toggleTag, type CitySort } from "@/game/cityList";
import { TAG_COLORS, emptyBook, tagsOf } from "@/game/tagbook";
import { usePrefs } from "@/state/prefs";
import { useTagBook } from "@/state/tags";
import { art } from "./art";
import { IconChevronDown, IconClose, IconCrossedSwords, IconSearch } from "./icons";
import { Empty, Medallion, Row, cx } from "./primitives";

/** A heart that fills from the bottom with loyalty (0–100), crimson under a gold rim. */
export const LoyaltyHeart = ({ loyalty }: { loyalty: number }) => {
  const pct = Math.max(0, Math.min(100, Math.round(loyalty)));
  const id = `lh-${pct}`;
  const path = "M12 21s-7.5-4.6-9.6-9.3C.9 8.4 2.8 4.6 6.4 4.1c2.2-.3 4 .8 5.6 2.9 1.6-2.1 3.4-3.2 5.6-2.9 3.6.5 5.5 4.3 4 7.6C19.5 16.4 12 21 12 21z";
  return (
    <span className={cx("lheart", pct < 100 && "low", pct <= 50 && "danger")} aria-label={`Loyalty ${pct}%`} title={`Loyalty ${pct} of 100`}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <defs>
          <clipPath id={id}><rect x="0" y={24 - (24 * pct) / 100} width="24" height={(24 * pct) / 100} /></clipPath>
          <linearGradient id={`${id}-f`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#E0604A" /><stop offset="1" stopColor="#8E2219" /></linearGradient>
        </defs>
        <path d={path} fill="#151A1C" />
        <path d={path} fill={`url(#${id}-f)`} clipPath={`url(#${id})`} />
        <path d={path} fill="none" stroke="#D2A956" strokeWidth="1.3" strokeLinejoin="round" />
      </svg>
      <b>{pct}%</b>
    </span>
  );
};

const NO_BOOK = emptyBook();

export function CityPickList({ wid, cities, current, onPick, empty = "No cities to choose from.", meta }: {
  /** the world: its tag book (state/tags.ts) gives the tags to show, search, filter and sort by */
  wid: number;
  cities: PlayerVillage[]; current: number | null; onPick: (c: PlayerVillage) => void; empty?: string;
  /** the line under the name; default "(x, y) · R563 · points pts · N incoming attacks" */
  meta?: (c: PlayerVillage) => string;
}) {
  const book = useTagBook(wid).data ?? NO_BOOK;
  const sort = usePrefs((s) => s.citySort);
  const setPrefs = usePrefs((s) => s.set);
  const [query, setQuery] = useState("");
  const [regionPick, setRegion] = useState<number | null>(null);
  const [tagPicks, setTags] = useState<string[]>([]);
  const incSortPref = usePrefs((s) => s.cityIncomingSort);
  const [incPick, setIncPick] = useState<IncomingFilter>(null);
  const anyInc = useMemo(() => totalIncoming(cities) > 0, [cities]);
  const now = useNow(anyInc ? 1000 : 60_000);
  const { book: inc } = useIncomingBook(wid, cities, anyInc);
  const summary = useMemo(() => incomingSummary(cities, inc, now), [cities, inc, now]);
  const incSort = anyInc && incSortPref;
  // a summary chip that no longer has cities behind it lets go
  const incFilter: IncomingFilter = !anyInc ? null : incPick === "urgent" && !summary.urgent ? null : incPick;
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (typeof window.matchMedia === "function" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) searchRef.current?.focus({ preventScroll: true });
  }, []);
  const regions = useMemo(() => regionCounts(cities), [cities]);
  const tags = useMemo(() => tagCounts(cities, book), [cities, book]);
  // a choice that no longer fits (the tag was deleted, no city is left in that region) lets go
  const region = regionPick !== null && regions.some((r) => r.continent === regionPick) ? regionPick : null;
  const picked = useMemo(
    () => tagPicks.filter((id) => (id === NO_TAG ? tags.untagged > 0 : tags.tags.some((t) => t.tag.id === id))),
    [tagPicks, tags],
  );
  const base = useMemo(() => cityList(cities, book, { query, region, tags: picked, sort }), [cities, book, query, region, picked, sort]);
  const list = useMemo(() => {
    const shown = filterIncoming(base, inc, now, incFilter);
    return incSort ? sortByIncoming(shown, inc, now) : shown;
  }, [base, inc, now, incFilter, incSort]);
  const groups = useMemo(() => (incSort ? incomingGroups(list, inc, now) : null), [incSort, list, inc, now]);
  const narrowed = !!query.trim() || region !== null || picked.length > 0 || incFilter !== null;
  const allOf = picked.length > 1;
  const clear = () => {
    setQuery("");
    setRegion(null);
    setTags([]);
    setIncPick(null);
  };
  const toggleInc = (f: Exclude<IncomingFilter, null>) => setIncPick(incFilter === f ? null : f);
  // the incoming count moved to its own red line under this one (Oct 14 2026)
  const line = meta ?? ((c: PlayerVillage) => `${fmtCoords(c.coords)} · ${regionLabel(c.continent)} · ${fmtNum(c.points)} pts`);
  const row = (c: PlayerVillage) => {
    const here = c.id === current;
    const worn = tagsOf(book, c.id);
    const n = incomingCount(c);
    const next = n ? nextOf(inc, c.id, now) : null;
    const urgent = next !== null && next - now < URGENT_MS;
    return (
      <Row key={c.id} as="button" alert={n > 0} className={cx("cp-row", here && "here", urgent && "cp-urgent")} aria-current={here ? "true" : undefined} onClick={() => onPick(c)}>
        <span className="cp-medal">
          <Medallion src={art.med("b_hq")} alt="" size={54} glow={urgent ? "crim" : undefined} />
          {n > 0 && <span className="cp-medal-inc" aria-hidden="true"><IconCrossedSwords /></span>}
        </span>
        <div className="txt">
          <div className="line"><b className="cp-name">{c.city_name}{here && <span className="sr-only"> (you are here)</span>}</b></div>
          <div className="meta">{line(c)}</div>
          {n > 0 && (
            <div className="cp-incline">
              <span className="cp-inc">
                <IconCrossedSwords />{fmtNum(n)} incoming{next !== null && <> · lands <b>{fmtDuration(next - now)}</b></>}
                {urgent && <i className="cp-blink" aria-hidden="true" />}
              </span>
            </div>
          )}
          {worn.length > 0 && (
            <div className="cp-tags">
              {worn.map((t) => <span key={t.id} className="cp-tag" style={{ ["--tag" as string]: TAG_COLORS[t.color] } as CSSProperties}><i aria-hidden="true" />{t.name}</span>)}
            </div>
          )}
        </div>
        <div className="right"><LoyaltyHeart loyalty={Number(c.loyalty ?? 100)} /></div>
      </Row>
    );
  };
  return (
    <div className="city-pick-box">
      {cities.length > 1 && (
        <div className="cp-tools">
          <label className="cp-search">
            <IconSearch />
            <input
              ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, coords, region or tag" aria-label="Search your cities"
              autoComplete="off" autoCorrect="off" spellCheck={false} enterKeyHint="go"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing && list.length) {
                  e.preventDefault();
                  onPick(list[0]);
                }
              }}
            />
            {query && <button type="button" aria-label="Clear the search" onClick={() => setQuery("")}><IconClose /></button>}
          </label>
          <div className={cx("cp-ctrls", anyInc && "three")}>
            <label className="cp-select">
              <span className="lbl" aria-hidden="true">Sort</span>
              <select aria-label="Sort cities by" value={sort} onChange={(e) => setPrefs({ citySort: e.target.value as CitySort })}>
                {CITY_SORTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <IconChevronDown />
            </label>
            <label className={cx("cp-select", region !== null && "on")}>
              <span className="lbl" aria-hidden="true">Region</span>
              <select aria-label="Show cities in region" value={region ?? ""} onChange={(e) => setRegion(e.target.value === "" ? null : Number(e.target.value))}>
                <option value="">All regions</option>
                {regions.map((r) => <option key={r.continent} value={r.continent}>{`${regionLabel(r.continent)} (${r.count})`}</option>)}
              </select>
              <IconChevronDown />
            </label>
            {anyInc && (
              <button type="button" className="cp-incsort" aria-pressed={incSort} onClick={() => setPrefs({ cityIncomingSort: !incSort })}
                title={incSort ? "Back to your Sort" : "Soonest landing first"}>
                <span className="lbl" aria-hidden="true">{incSort ? "Sorted by" : "Sort by"}</span>
                <span className="val"><IconCrossedSwords />Incoming</span>
              </button>
            )}
          </div>
          {tags.tags.length > 0 && (
            <div className={cx("cp-tagpick", picked.length > 0 && "on")} role="group" aria-label="Show cities with all of these tags">
              <span className="lbl" aria-hidden="true">Tags</span>
              <div className="cp-chips">
                {tags.tags.map(({ tag: t, count }) => (
                  <button
                    key={t.id} type="button" className="cp-tag cp-chip" aria-pressed={picked.includes(t.id)} onClick={() => setTags(toggleTag(picked, t.id))}
                    style={{ ["--tag" as string]: TAG_COLORS[t.color] } as CSSProperties}
                  ><i aria-hidden="true" />{t.name}<small>{count}</small></button>
                ))}
                {tags.untagged > 0 && (
                  <button type="button" className="cp-tag cp-chip none" aria-pressed={picked.includes(NO_TAG)} onClick={() => setTags(toggleTag(picked, NO_TAG))}>
                    No tag<small>{tags.untagged}</small>
                  </button>
                )}
              </div>
            </div>
          )}
          {anyInc && (
            <div className="cp-incsum" role="group" aria-label="Incoming attacks">
              {summary.urgent > 0 && (
                <button type="button" className="cp-sumchip urgent" aria-pressed={incFilter === "urgent"} onClick={() => toggleInc("urgent")}>{fmtNum(summary.urgent)} land &lt; 10 min</button>
              )}
              <button type="button" className="cp-sumchip hit" aria-pressed={incFilter === "hit"} onClick={() => toggleInc("hit")}>{fmtNum(summary.hit)} under attack</button>
              <button type="button" className="cp-sumchip" aria-pressed={incFilter === "clear"} onClick={() => toggleInc("clear")}>{fmtNum(summary.clear)} clear</button>
              {summary.next !== null && <span className="cp-sumnext">next lands <b>{fmtDuration(summary.next - now)}</b></span>}
            </div>
          )}
          {narrowed && (
            <div className="cp-count" role="status">
              <span>{list.length} of {plural(cities.length, "city", "cities")}{allOf && ` · with all ${picked.length} tags`}</span>
              <button type="button" className="cp-clear" onClick={clear}>Show all</button>
            </div>
          )}
        </div>
      )}
      <div className="rows city-pick">
        {groups
          ? groups.map((g) => (
              <section key={g.tier} className={cx("cp-group", `t-${g.tier}`)} aria-label={TIER_LABEL[g.tier]}>
                <h4 className="cp-ghead">
                  {TIER_LABEL[g.tier]}
                  <span>{g.tier === "clear" ? plural(g.cities.length, "city", "cities") : `${plural(g.cities.length, "city", "cities")} · ${plural(g.attacks, "attack")}`}</span>
                </h4>
                {g.cities.map(row)}
              </section>
            ))
          : list.map(row)}
        {!cities.length && <Empty>{empty}</Empty>}
        {cities.length > 0 && !list.length && (
          <Empty>{query.trim() ? `No city matches “${query.trim()}”${region !== null || picked.length ? " with these filters" : ""}.` : allOf && region === null ? "No city wears all of these tags." : "No city matches these filters."}</Empty>
        )}
      </div>
    </div>
  );
}
